import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { EmployeesConst } from "../employees/const";
import { EmployeesListModel } from "../employees/employees-list.model";
import { EmployeesListService } from "../employees/employees-list.service";
import { EmployeesDialogDeleteComponent } from "./employees-dialog-delete.component";
import { EmployeesFormComponent } from "./employees-form.component";
import { BookingStaffSyncDialogComponent } from "./booking-staff-sync-dialog.component";
import { BookingBulkSyncDialogComponent } from "./booking-bulk-sync-dialog.component";
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { DummyComponent } from "../../shared/dummy.component";
import { Router } from "@angular/router";
import { ConfigurarAccesoEmpleadoComponent } from "./acceso/config-acceso.component";
import { Utils } from "../../shared/utils";
import { consts } from "../../consts";

@Component({
    selector: "app-employees-list",
    templateUrl: "./employees-list.component.html",
    styleUrls: ["./employees-list.component.css"],
    standalone: false
})
export class EmployeesListComponent implements OnInit {
  dataSource: EmployeesListModel[] = [];
  headerTable: any[] = EmployeesConst.headerTable;
  displayColumns: any[] = [];
  ready = false;
  rowId: number;
  tableKey: number = 0;

  hideInactive: boolean = true;
  selectedTab: number = 0;

  showTable: boolean = true;
  filteredDataSource: EmployeesListModel[] = [];

  constructor(
    public EmployeesListService: EmployeesListService,
    public dialog: MatDialog,
    public dummy: DummyComponent,
    private router: Router,
    public snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  get empty(): boolean {
    return this.filteredDataSource.length === 0;
  }

  public ngOnInit() {
    this.initializeDisplayColumns();
    this.dataSource = [];
    this.EmployeesListService.getList().then((data) => {
      this.dataSource = data;
      this.applyFilter();
      this.ready = true;
    });
  }

  public applyFilter(): void {
    this.showTable = false;

    if (this.hideInactive) {
      this.filteredDataSource = this.dataSource.filter(emp => emp.active);
    } else {
      this.filteredDataSource = [...this.dataSource];
    }

    setTimeout(() => {
      this.showTable = true;
      this.cdr.detectChanges();
    }, 0);
  }

  public toggleHideInactive(): void {
    this.hideInactive = !this.hideInactive;
    this.applyFilter();
    this.cdr.detectChanges();
  }

  private initializeDisplayColumns(): void {
    this.displayColumns = [
      {
        field: 'name',
        title: 'Nombre',
        cell: (row) => `${row.name}`,
        class: (row) => 'employee-name',
      },
      {
        field: 'dni',
        title: 'DNI',
        cell: (row) => (row.dni !== null ? `${row.dni}` : '-'),
        class: (row) => '',
      },
      {
        field: 'category',
        title: 'Categoría',
        cell: (row) => (row.category !== null ? `${row.category}` : '-'),
        class: (row) => 'employee-category',
      },
      {
        field: 'datosAccesoConfigurados',
        title: 'Acceso configurado',
        cell: (row) => this.getBadgeText(row.datosAccesoConfigurados, 'acceso'),
        class: (row) => this.getBadgeClass(row.datosAccesoConfigurados),
      },
      {
        field: 'active',
        title: 'Activo',
        cell: (row) => this.getBadgeText(row.active, 'activo'),
        class: (row) => this.getBadgeClass(row.active),
      },
      {
        field: 'tieneRegistroHorasAbierto',
        title: 'Trabajando ahora',
        cell: (row) => this.getWorkingStatusText(row),
        class: (row) => this.getWorkingStatusClass(row.tieneRegistroHorasAbierto),
      },
      {
        field: 'activarGeolocalizacion',
        title: 'Geolocalización',
        cell: (row) => this.getBadgeText(row.activarGeolocalizacion, 'geo'),
        class: (row) => this.getBadgeClass(row.activarGeolocalizacion),
      },
    ];
  }

  private getBadgeText(value: boolean, type: string): string {
    if (value) {
      const icons = {
        acceso: '✓',
        activo: '✓',
        geo: '📍',
      };
      return `${icons[type] || '✓'} Sí`;
    } else {
      return '✗ No';
    }
  }

  private getBadgeClass(value: boolean): string {
    return value ? 'badge badge-success' : 'badge badge-secondary';
  }

  private getWorkingStatusText(row: any): string {
    if (row.tieneRegistroHorasAbierto) {
      if (row.duracionRegistroHorasAbierto) {
        return `⏱️ ${row.duracionRegistroHorasAbierto}`;
      }
      return '⏱️ Trabajando';
    }
    return '✗ No';
  }

  private getWorkingStatusClass(value: boolean): string {
    return value ? 'working-yes' : 'working-no';
  }

  public activeAccess(uid: number) {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.dialog
      .open(ConfigurarAccesoEmpleadoComponent, {
        // 480px es menor que el min-width: min(540px, 94vw) que exige la
        // clase compartida .cf-dlg (styles.css) usada por la cabecera/footer
        // de este dialog — con 480px el contenido se ensanchaba por encima
        // del contenedor real del diálogo y el botón "Guardar" quedaba
        // cortado. 540px alinea el ancho del dialog con lo que .cf-dlg espera.
        width: "540px",
        maxWidth: "96vw",
        maxHeight: "90vh",
        data: { empleado: this.dataSource.find((x) => x.uid == uid) },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result != null) {
          const index = this.dataSource.findIndex((x) => x.uid == result.uid);
          this.dataSource[index] = result;
          this.applyFilter();
        }
      });
  }

  public create(uid: number): void {
    this.dialog.open(EmployeesFormComponent, {
      width: "90%",
      maxWidth: "900px",
      maxHeight: "90vh",
      data: { uid: uid },
    });
  }

  public edit(uid: number): void {
    this.dialog.open(EmployeesFormComponent, {
      width: "90%",
      maxWidth: "900px",
      maxHeight: "90vh",
      data: { uid: uid },
    });
  }

  public delete(uid: number) {
    this.dialog
      .open(EmployeesDialogDeleteComponent, {
        data: { uid: uid },
      })
      .afterClosed()
      .subscribe((result) => {
        this.dummy.redirectTo(this.router.url);
      });
  }

  public displayInfo(message: string): void {
    const options = new MatSnackBarConfig();
    options.duration = 10000;
    this.snackBar.open(message, "", options);
  }

  public syncBooking(uid: number): void {
    const employee = this.dataSource.find(e => e.uid === uid);
    this.dialog.open(BookingStaffSyncDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { employeeId: uid, employeeName: employee?.name || '' }
    });
  }

  public bulkSync(): void {
    this.dialog.open(BookingBulkSyncDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      maxHeight: '90vh'
    });
  }

  public isEmployeeActive(uid: number): boolean {
    const empleado = this.dataSource.find((x) => x.uid === uid);
    return empleado != null && empleado.active ? true : false;
  }

  usuarioConAccesoConfigurado(uid: number): boolean {
    const usuario = this.dataSource.find((x) => x.uid == uid);
    return usuario != null && usuario.datosAccesoConfigurados;
  }
}